/* eslint-disable react/no-unknown-property */
import React from 'react';
import { ActionIcon, ICON_CLOSE } from '@doveit/bricks';
import objectPath from 'object-path';
import { cloneDeep } from 'lodash';
import { Property, PropertyPreview, PropertySearchItem } from '../../../providers/api/dtos';
import useProperty from '../../../property/hooks/use-property/useProperty';
import useScroogeProperty from '../../../property/hooks/use-scrooge-property/useScroogeProperty';
import { BrandTemplateAutofillDecodedDataset, BrandTemplateAutofillPropertyData } from '../../utils/autofill/types';
import * as styles from './TemplatePropertySelector.style';
import ViewPropertyPreview from '../../../property/containers/view-property-preview/ViewPropertyPreview';
import TemplateFieldsForm from '../../components/template-fields-form/TemplateFieldsForm';
import { PROPERTY_DATA_TEMPLATE_FIELD_CONFIG } from '../../pages/use-brand-template/config/brandTemplateFieldConfig';
import { mapFieldsConfigToTemplateFieldsInitialValues } from '../../components/template-fields-form/mappers';
import PropertyPhotosSelector, { PropertyPhotosSelectorSlotsConfig } from '../../../property/containers/property-photos-selector/PropertyPhotosSelector';
import { getFieldSlots } from '../../utils/autofill/autofill';
import PropertyAutoComplete from '../../../containers/property-autocomplete/PropertyAutoComplete';
import { Aggregator, Status } from '../../../domain/types';
import { useCurrentAgentSync } from '../../../hooks/use-agent/useAgent';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { toBrandTemplateAutofillPropertyData } from '../../utils/autofill/utils';

export interface TemplatePropertySelectorProps {
  propertyId?: NonNullable<Property['id']>,
  decodedDataset: BrandTemplateAutofillDecodedDataset,
  disabled?: boolean,
  onPropertySelected?: (property: PropertySearchItem | PropertyPreview | null) => void,
  onPropertyDeselected?: () => void,
  onDataChange?: (propertyData: BrandTemplateAutofillPropertyData) => void,
}

export const TemplatePropertySelector: React.FC<TemplatePropertySelectorProps> = ({
  propertyId,
  decodedDataset,
  disabled,
  onPropertySelected,
  onPropertyDeselected,
  onDataChange,
}) => {
  const [id, setId] = React.useState<NonNullable<Property['id']> | undefined>(propertyId);
  const { data: currentAgent } = useCurrentAgentSync();
  const { data: property } = useProperty(id);
  const { data: scroogeProperty } = useScroogeProperty(id);
  const [propertyData, setPropertyData] = React.useState<BrandTemplateAutofillPropertyData>();

  const agentEmailsFilter = React.useMemo(() => {
    if (!currentAgent) return undefined;

    if (currentAgent.canManage && currentAgent.canManage.length > 0) {
      return removeNullAndUndefinedValues([currentAgent.email.work, ...currentAgent.canManage.map((agent) => agent.email.work)]);
    }

    return [currentAgent.email.work!];
  }, [currentAgent]);

  React.useEffect(() => {
    setId(propertyId);
  }, [propertyId]);

  React.useEffect(() => {
    onDataChange?.(propertyData || {});
  }, [propertyData, onDataChange]);

  React.useEffect(() => {
    if (!property && !scroogeProperty) {
      return;
    }

    setPropertyData((prev) => ({
      ...(prev ?? {}),
      ...(toBrandTemplateAutofillPropertyData(property, scroogeProperty) ?? {}),
    }));
  }, [property, scroogeProperty]);

  const onPropertyFieldChange = React.useCallback((fieldName: string, value: unknown) => {
    setPropertyData((prev) => {
      const next = cloneDeep(prev ?? {});
      objectPath.set(next, fieldName, value);

      return next;
    });
  }, []);

  const onPhotosChange = React.useCallback((photos: PropertyPhotosSelectorSlotsConfig) => {
    setPropertyData((prev) => ({
      ...prev,
      photos,
    }));
  }, []);

  const filteredPropertyFields = React.useMemo(
    () => PROPERTY_DATA_TEMPLATE_FIELD_CONFIG.filter(({ name }) => decodedDataset.variables.some((v) => v.raw === name)),
    [decodedDataset.variables],
  );

  const propertyInitialValues = React.useMemo(
    () => mapFieldsConfigToTemplateFieldsInitialValues(filteredPropertyFields, propertyData),
    [filteredPropertyFields, propertyData],
  );

  const selectablePropertyPhotos = React.useMemo(
    () => (decodedDataset ? getFieldSlots(decodedDataset.variables, 'property_data', 'photos').length : 0),
    [decodedDataset],
  );

  const clearPropertySelection = React.useCallback(() => {
    setPropertyData(undefined);
    setId(undefined);

    onPropertyDeselected?.();
  }, [onPropertyDeselected]);

  const onInternalPropertySelected = React.useCallback((selectedProperty: PropertySearchItem | PropertyPreview | null) => {
    setId(selectedProperty?.id);
    onPropertySelected?.(selectedProperty);
  }, [onPropertySelected]);

  return (
    <>
      {!id && (
        <div css={styles.autocompleteWrapper}>
          <PropertyAutoComplete
            aria-label="Seleziona un immobile"
            placeholder="Cerca immobile"
            aggregators={Aggregator.DOVE_IT}
            status={[
              Status.LIVE,
              Status.ANTEPRIMA,
              Status.PROPOSTA,
              Status.VENDUTO,
            ]}
            onSuggestionSelected={onInternalPropertySelected}
            maxSuggestions={10}
            agentEmail={agentEmailsFilter}
          />
        </div>
      )}
      {id && (
        <div>
          <div css={styles.previewWrapper}>
            <ViewPropertyPreview
              propertyId={id}
              primaryActions={[
                <ActionIcon
                  key="deselect-property"
                  icon={{ path: ICON_CLOSE }}
                  label="Deseleziona l'immobile"
                  aria-label="Annulla la selezione dell'immobile"
                  disabled={disabled}
                  onClick={clearPropertySelection}
                  size="S"
                />,
              ]}
            />
          </div>
          <div css={styles.formWrapper}>
            <TemplateFieldsForm
              fields={filteredPropertyFields}
              initialValues={propertyInitialValues}
              disabled={disabled}
              onFieldChange={onPropertyFieldChange}
            />
          </div>
          {selectablePropertyPhotos > 0 && (
            <div css={styles.extraDataWrapper}>
              <PropertyPhotosSelector
                propertyId={id}
                totalSlots={selectablePropertyPhotos}
                onChange={onPhotosChange}
                disabled={disabled}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TemplatePropertySelector;
